export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  attachments?: File[];
}

export interface SelfOnboardingFormData {
  fullName: string;
  email: string;
  phone: string;
  documentUrls: string[];
}

export interface CustomerSurveyFormData {
  accountNumber: string;
  mobileNumber: string;
  complaintMessage: string;

}

// Simulate API delay for better UX
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const formService = {
  submitContact: async (data: ContactFormData) => {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(
          `Attempting to submit contact form (attempt ${attempt}/${maxRetries})`
        );

        let response;

        // Check if there are attachments to determine the request format
        if (data.attachments && data.attachments.length > 0) {
          // Send as FormData when there are file attachments
          const formData = new FormData();
          formData.append("name", data.name);
          formData.append("email", data.email);
          formData.append("phone", data.phone || "");
          formData.append("subject", data.subject);
          formData.append("message", data.message);

          // Append each attachment file
          data.attachments.forEach((file) => {
            if (file) {
              formData.append("attachments", file);
            }
          });

          response = await fetch(
            "https://mybank-backend-4wfq.onrender.com/api/email/contact",
            {
              method: "POST",
              mode: "cors",
              credentials: "omit",
              body: formData, // Don't set Content-Type header for FormData
            }
          );
        } else {
          // Send as JSON when there are no attachments
          response = await fetch(
            "https://mybank-backend-4wfq.onrender.com/api/email/contact",
            {
              method: "POST",
              mode: "cors",
              credentials: "omit",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                name: data.name,
                email: data.email,
                phone: data.phone,
                subject: data.subject,
                message: data.message,
              }),
            }
          );
        }

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorMessage;
          } catch {
            // If not JSON, use the text as error message
            errorMessage = errorText || errorMessage;
          }

          throw new Error(errorMessage);
        }

        const result = await response.json();

        console.log(
          "📧 Contact form sent successfully to callcenter@mybank.so"
        );
        if (data.attachments && data.attachments.length > 0) {
          console.log(
            "📎 Attachments:",
            data.attachments.map((f) => f.name).join(", ")
          );
        }

        return {
          success: true,
          message:
            result.message ||
            "Your message has been sent successfully! We will get back to you soon.",
          timestamp: result.timestamp,
        };
      } catch (error: any) {
        lastError = error;
        console.error(
          `Contact form submission error (attempt ${attempt}):`,
          error
        );

        // If it's a network error and we have more attempts, wait and retry
        if (
          attempt < maxRetries &&
          (error.name === "TypeError" ||
            error.message.includes("fetch") ||
            error.message.includes("network") ||
            error.message.includes("CORS"))
        ) {
          console.log(`Retrying in ${attempt * 1000}ms...`);
          await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          continue;
        }

        // If it's not a retryable error or we've exhausted retries, throw
        break;
      }
    }

    // If we get here, all attempts failed
    const errorMessage =
      lastError?.message || "Failed to send contact form. Please try again.";

    // Provide helpful error messages based on the error type
    if (errorMessage.includes("CORS")) {
      throw new Error(
        "Connection blocked by browser security. Please try refreshing the page or contact us directly at callcenter@mybank.so"
      );
    } else if (
      errorMessage.includes("fetch") ||
      errorMessage.includes("network")
    ) {
      throw new Error(
        "Network connection error. Please check your internet connection and try again, or contact us directly at callcenter@mybank.so"
      );
    } else if (errorMessage.includes("500")) {
      throw new Error(
        "Server error. Our team has been notified. Please try again later or contact us directly at callcenter@mybank.so"
      );
    }

    throw new Error(errorMessage);
  },

  submitSelfOnboarding: async (data: {
    fullName: string;
    email: string;
    phone: string;
    documents: File[];
  }) => {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(
          `Attempting to submit self-onboarding form (attempt ${attempt}/${maxRetries})`
        );

        // Create FormData for file upload to backend
        const formData = new FormData();
        formData.append("fullName", data.fullName);
        formData.append("email", data.email);
        formData.append("phone", data.phone);

        // Append each document file
        data.documents.forEach((file, index) => {
          if (file) {
            formData.append("documents", file);
          }
        });

        // Send directly to backend email transmitter with file attachments
        const response = await fetch(
          "https://mybank-backend-4wfq.onrender.com/api/email/onboarding",
          {
            method: "POST",
            mode: "cors",
            credentials: "omit",
            body: formData, // Don't set Content-Type header for FormData
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorMessage;
          } catch {
            errorMessage = errorText || errorMessage;
          }

          throw new Error(errorMessage);
        }

        const result = await response.json();

        console.log(
          "📧 Self-onboarding application sent successfully to callcenter@mybank.so"
        );
        console.log(
          "📄 Documents attached:",
          data.documents.map((f) => f.name).join(", ")
        );
        console.log("🆔 Application ID:", result.applicationId);

        return {
          success: true,
          message:
            result.message ||
            "Your account opening request has been submitted successfully. We will review your documents and contact you within 2-3 business days.",
          id: result.applicationId,
          documentsReceived: result.documentsReceived,
          timestamp: result.timestamp,
          emailSent: true,
        };
      } catch (error: any) {
        lastError = error;
        console.error(
          `Self-onboarding submission error (attempt ${attempt}):`,
          error
        );

        // If it's a network error and we have more attempts, wait and retry
        if (
          attempt < maxRetries &&
          (error.name === "TypeError" ||
            error.message.includes("fetch") ||
            error.message.includes("network") ||
            error.message.includes("CORS"))
        ) {
          console.log(`Retrying in ${attempt * 1000}ms...`);
          await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          continue;
        }

        break;
      }
    }

    // If we get here, all attempts failed
    const errorMessage =
      lastError?.message || "Failed to submit application. Please try again.";

    // Provide helpful error messages based on the error type
    if (errorMessage.includes("CORS")) {
      throw new Error(
        "Connection blocked by browser security. Please try refreshing the page or contact us directly at callcenter@mybank.so"
      );
    } else if (
      errorMessage.includes("fetch") ||
      errorMessage.includes("network")
    ) {
      throw new Error(
        "Network connection error. Please check your internet connection and try again, or contact us directly at callcenter@mybank.so"
      );
    } else if (errorMessage.includes("500")) {
      throw new Error(
        "Server error. Our team has been notified. Please try again later or contact us directly at callcenter@mybank.so"
      );
    }

    throw new Error(errorMessage);
  },

  submitCustomerSurvey: async (data: CustomerSurveyFormData) => {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(
          `Attempting to submit customer survey (attempt ${attempt}/${maxRetries})`
        );

        const response = await fetch(
          "https://mybank-backend-4wfq.onrender.com/api/forms/customer-survey",
          {
            method: "POST",
            mode: "cors",
            credentials: "omit",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorMessage;
          } catch {
            errorMessage = errorText || errorMessage;
          }

          throw new Error(errorMessage);
        }

        const result = await response.json();

        console.log(
          "📧 Customer survey sent successfully to callcenter@mybank.so"
        );
        console.log("📊 Survey data:", {
          accountNumber: data.accountNumber,
          mobileNumber: data.mobileNumber,
          complaintMessage: data.complaintMessage,
        });

        return {
          success: true,
          message:
            result.message ||
            "Thank you for your feedback! Your survey has been submitted successfully.",
          timestamp: result.timestamp,
        };
      } catch (error: any) {
        lastError = error;
        console.error(
          `Customer survey submission error (attempt ${attempt}):`,
          error
        );

        // If it's a network error and we have more attempts, wait and retry
        if (
          attempt < maxRetries &&
          (error.name === "TypeError" ||
            error.message.includes("fetch") ||
            error.message.includes("network") ||
            error.message.includes("CORS"))
        ) {
          console.log(`Retrying in ${attempt * 1000}ms...`);
          await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          continue;
        }

        break;
      }
    }

    // If we get here, all attempts failed
    const errorMessage =
      lastError?.message || "Failed to submit survey. Please try again.";

    // Provide helpful error messages based on the error type
    if (errorMessage.includes("CORS")) {
      throw new Error(
        "Connection blocked by browser security. Please try refreshing the page or contact us directly at callcenter@mybank.so"
      );
    } else if (
      errorMessage.includes("fetch") ||
      errorMessage.includes("network")
    ) {
      throw new Error(
        "Network connection error. Please check your internet connection and try again, or contact us directly at callcenter@mybank.so"
      );
    } else if (errorMessage.includes("500")) {
      throw new Error(
        "Server error. Our team has been notified. Please try again later or contact us directly at callcenter@mybank.so"
      );
    }

    throw new Error(errorMessage);
  },

  submitComplaintToCore: async (data: CustomerSurveyFormData) => {
    const proxyUrl = "https://mybank-backend-4wfq.onrender.com/api/forms/register-complaint";
    // const proxyUrl = "http://localhost:5000/api/forms/register-complaint";

    const response = await fetch(proxyUrl, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        accountNumber: data.accountNumber,
        mobileNumber: data.mobileNumber,
        complaintMessage: data.complaintMessage,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let message = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const json = JSON.parse(errorText);
        message = json.message || message;
      } catch {
        message = errorText || message;
      }
      throw new Error(message);
    }

    const result = await response.json();
    return {
      success: true,
      message: result?.message || "Complaint registered successfully with core system.",
      raw: result,
    };
  },
};

export default formService;
